import React from 'react';

/**
 * 나이스 시간표 도우미(Chrome 확장, extensions/neis-timetable) 설치 여부 감지.
 * 확장의 app-bridge.js 가 이 페이지에서
 *  - document.documentElement.dataset.cpNeisExt = 버전 (+ cpNeisOrigin = 마지막으로 연 나이스 주소)
 *  - window 에 'cp-neis-ext-ready' 이벤트
 *  - window.postMessage({ type: 'cp-neis-ext-ping' }) 에 { type: 'cp-neis-ext-pong', version, neisOrigin } 응답
 * 을 해 준다. 짧은 대기 후에도 신호가 없으면 '미설치'로 본다(나중에 신호가 오면 다시 '설치됨').
 */
export type NeisExtensionStatus = 'checking' | 'installed' | 'absent';

export type NeisExtensionInfo = {
  status: NeisExtensionStatus;
  version: string;
  /** 확장이 기억한 마지막 나이스 주소(예: https://sen.neis.go.kr). 모르면 '' */
  neisOrigin: string;
};

const PING = 'cp-neis-ext-ping';
const PONG = 'cp-neis-ext-pong';
const READY_EVENT = 'cp-neis-ext-ready';

function safeNeisOrigin(raw: unknown): string {
  if (typeof raw !== 'string' || !raw) return '';
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:') return '';
    if (u.hostname !== 'neis.go.kr' && !u.hostname.endsWith('.neis.go.kr')) return '';
    return u.origin;
  } catch {
    return '';
  }
}

function readDataset(): { version: string; neisOrigin: string } | null {
  if (typeof document === 'undefined') return null;
  const ds = document.documentElement.dataset;
  const version = (ds.cpNeisExt || '').trim();
  if (!version) return null;
  return { version: version.slice(0, 20), neisOrigin: safeNeisOrigin(ds.cpNeisOrigin) };
}

export function useNeisExtension(enabled = true, timeoutMs = 1200): NeisExtensionInfo {
  const [info, setInfo] = React.useState<NeisExtensionInfo>(() => {
    const d = readDataset();
    return d ? { status: 'installed', ...d } : { status: 'checking', version: '', neisOrigin: '' };
  });

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    let found = false;
    const markInstalled = (version: string, neisOrigin: string) => {
      found = true;
      setInfo((prev) =>
        prev.status === 'installed' && prev.version === version && prev.neisOrigin === neisOrigin
          ? prev
          : { status: 'installed', version, neisOrigin },
      );
    };
    const fromDataset = () => {
      const d = readDataset();
      if (d) markInstalled(d.version, d.neisOrigin);
    };
    const onMessage = (e: MessageEvent) => {
      if (e.source !== window || !e.data || typeof e.data !== 'object') return;
      if (e.data.type !== PONG) return;
      const version = typeof e.data.version === 'string' ? e.data.version.slice(0, 20) : '';
      if (version) markInstalled(version, safeNeisOrigin(e.data.neisOrigin));
    };
    fromDataset();
    window.addEventListener('message', onMessage);
    window.addEventListener(READY_EVENT, fromDataset);
    window.postMessage({ type: PING }, window.location.origin);
    const timer = window.setTimeout(() => {
      if (!found) {
        fromDataset();
        if (!found) setInfo((prev) => (prev.status === 'checking' ? { ...prev, status: 'absent' } : prev));
      }
    }, timeoutMs);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      window.removeEventListener(READY_EVENT, fromDataset);
    };
  }, [enabled, timeoutMs]);

  return info;
}
